import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { DesignHuddleService } from '@app/shared/services/design-huddle/design-huddle.service';
import { DesignHuddleTemplate } from '@app/shared/models/design-huddle';
import { NotificationService } from '@app/core/services/notification/notification.service';
import { LoaderService } from '@app/core/services/loader/loader.service';
import { ErrorhandlerService } from '@app/shared/services/errorhandler/errorhandler.service';

/**
 * Design Huddle Template Selector Component
 * Displays available templates and allows user to select one
 */
@Component({
  selector: 'app-design-huddle-template-selector',
  templateUrl: './design-huddle-template-selector.component.html',
  styleUrls: ['./design-huddle-template-selector.component.scss']
})
export class DesignHuddleTemplateSelectorComponent implements OnInit {
  @Input() selectedCategory: string = '';
  @Output() onTemplateSelected = new EventEmitter<DesignHuddleTemplate>();
  @Output() onCancel = new EventEmitter<void>();

  templates: DesignHuddleTemplate[] = [];
  filteredTemplates: DesignHuddleTemplate[] = [];
  categories: string[] = [];
  selectedTemplate: DesignHuddleTemplate | null = null;

  // Pagination
  currentPage: number = 1;
  totalPages: number = 1;
  totalCount: number = 0;
  perPage: number = 20;

  // Search
  searchQuery: string = '';

  // UI State
  isLoading: boolean = false;
  viewMode: 'grid' | 'list' = 'grid';

  constructor(
    private designHuddleService: DesignHuddleService,
    private notificationService: NotificationService,
    private loader: LoaderService,
    private errorHandler: ErrorhandlerService
  ) { }

  ngOnInit(): void {
    this.loadTemplates();
  }

  /**
   * Load templates from Design Huddle
   */
  loadTemplates(page: number = 1): void {
    this.isLoading = true;
    this.loader.show();

    this.designHuddleService.getTemplates(this.selectedCategory, page, this.perPage)
      .subscribe({
        next: (response) => {
          this.loader.hide();
          this.isLoading = false;

          if (response.StatusCode === 200 && response.Data?.ResponseData) {
            const data = response.Data.ResponseData;
            this.templates = data.templates || [];
            this.filteredTemplates = [...this.templates];
            this.currentPage = data.currentPage || 1;
            this.totalPages = data.totalPages || 1;
            this.totalCount = data.totalCount || 0;

            // Extract unique categories
            this.extractCategories();

            // Apply search filter if exists
            if (this.searchQuery) {
              this.applySearch();
            }
          } else {
            this.notificationService.error('Failed to load templates', 'Error');
          }
        },
        error: (error) => {
          this.loader.hide();
          this.isLoading = false;
          this.errorHandler.handleError(error);
          this.notificationService.error('Failed to load templates', 'Error');
        }
      });
  }

  /**
   * Extract unique categories from templates
   */
  private extractCategories(): void {
    const categorySet = new Set<string>();
    this.templates.forEach(template => {
      if (template.category) {
        categorySet.add(template.category);
      }
    });
    this.categories = Array.from(categorySet).sort();
  }

  /**
   * Filter templates by category
   */
  filterByCategory(category: string): void {
    this.selectedCategory = category;
    this.currentPage = 1;
    this.loadTemplates(1);
  }

  /**
   * Clear category filter
   */
  clearCategoryFilter(): void {
    this.selectedCategory = '';
    this.currentPage = 1;
    this.loadTemplates(1);
  }

  /**
   * Apply search filter
   */
  applySearch(): void {
    if (!this.searchQuery.trim()) {
      this.filteredTemplates = [...this.templates];
      return;
    }

    const query = this.searchQuery.toLowerCase();
    this.filteredTemplates = this.templates.filter(template =>
      template.name.toLowerCase().includes(query) ||
      (template.description && template.description.toLowerCase().includes(query)) ||
      template.category.toLowerCase().includes(query)
    );
  }

  /**
   * Clear search
   */
  clearSearch(): void {
    this.searchQuery = '';
    this.applySearch();
  }

  /**
   * Select a template
   */
  selectTemplate(template: DesignHuddleTemplate): void {
    this.selectedTemplate = template;
  }

  /**
   * Confirm template selection
   */
  confirmSelection(): void {
    if (!this.selectedTemplate) {
      this.notificationService.info('Please select a template', 'Info');
      return;
    }

    this.onTemplateSelected.emit(this.selectedTemplate);
  }

  /**
   * Cancel template selection
   */
  cancel(): void {
    this.onCancel.emit();
  }

  /**
   * Navigate to next page
   */
  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.loadTemplates(this.currentPage);
    }
  }

  /**
   * Navigate to previous page
   */
  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.loadTemplates(this.currentPage);
    }
  }

  /**
   * Go to specific page
   */
  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadTemplates(page);
    }
  }

  /**
   * Toggle view mode
   */
  toggleViewMode(): void {
    this.viewMode = this.viewMode === 'grid' ? 'list' : 'grid';
  }

  /**
   * Get page numbers for pagination
   */
  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPagesToShow = 5;
    const halfRange = Math.floor(maxPagesToShow / 2);

    let startPage = Math.max(1, this.currentPage - halfRange);
    let endPage = Math.min(this.totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  }
}
